import React, { useState, useCallback } from "react";
import { useIntl } from "react-intl";
import { Link, useHistory } from "react-router-dom";
import MultiInput from "../../components/MultiInput";
import InputErrorMessage from "../../components/InputErrorMessage";
import { validateCpf, validateCnpj } from "../../utils/validate";
import { processOperation, processOperationKml } from "../../api";
import { formatCpfCnpj } from "../../utils/format";
import { trackEvent } from "../../utils/analytics";
import AoiUpload from "./components/AoiUpload";
import ConfirmationModal from "./components/ConfirmationModal";
import "./styles.scss";

const CONFIRMATION_MODAL = "CONFIRMATION_MODAL";
const LOADING_SAVE = "LOADING_SAVE";

const NewOperation = ({ setError }) => {
  const history = useHistory();
  const { formatMessage } = useIntl();
  const [documents, setDocuments] = useState([]);
  const [specificUses, setSpecificUses] = useState([]);
  const [invalidDocuments, setInvalidDocuments] = useState([]);
  const [aoiInfo, setAoiInfo] = useState(null);
  const [title, setTitle] = useState("");
  const [bacenReference, setBacenReference] = useState("");
  const [contract, setContract] = useState("");
  const [nonCultivatedArea, setNonCultivatedArea] = useState("");
  const [loadingStep, setLoadingStep] = useState(null);
  const [openModal, setOpenModal] = useState(null);
  const [isPronaf, setIsPronaf] = useState(false);

  const handleExecute = async () => {
    setLoadingStep(LOADING_SAVE);

    const operation = {};

    let kmls = [];
    if (aoiInfo) {
      kmls = [...aoiInfo];
    }
    // setError(formatMessage({ id: "validacao-kml" }));
    // return;

    if (specificUses.length > 0)
      operation.uso_especifico = specificUses.map(doc => doc.label);
    else 
      operation.uso_especifico = [];

    if (documents.length > 0)
      operation.cpf_cnpj = documents.map(doc => doc.label);
    // else {
    //   setError(formatMessage({ id: "validacao-documentos" }));
    //   return;
    // }

    if (bacenReference.length > 0) {
      operation.referencia_bacen = bacenReference;
    } else {
      setError(formatMessage({ id: "validacao-referencia-bacen" }));
      return;
    }

    if (contract.length >= 0) {
      operation.contrato = contract;
    } else {
      setError(formatMessage({ id: "validacao-contrato" }));
      return;
    }

    if (title.length > 0) operation.nome = title;
    if (nonCultivatedArea.length > 0)
      operation.area_nao_cultivada = nonCultivatedArea;

    operation.is_pronaf = isPronaf;

    await processOperation(operation)
      .then(async res => {
        // console.log(res);
        for (let fileContent of kmls) {
          try {
            await processOperationKml(
              res.data.b3_lote_id,
              fileContent.formData
            );
          } catch (err) {
            setError(
              `${
                err?.response?.data?.message ||
                formatMessage({ id: "erro-api" })
              } | KML: ${fileContent.fileName}`
            );
          }
        }

        history.push(`/operation-results/${res.data.b3_lote_id}`);
      })
      .catch(err => {
        const errorMessage = err.response?.data?.message;
        setError(
          errorMessage ? (
            <>
              {errorMessage}{" "}
              <Link to="/contact">
                {formatMessage({ id: "ir-para-contato" })}
              </Link>
            </>
          ) : (
            formatMessage({ id: "executar-analise-erro" })
          )
        );

        setOpenModal(null);
      });

    setLoadingStep(null);
  };

  const formatDocument = useCallback(
    unformattedValue => {
      const value = unformattedValue.replace(/[. /-]/g, "");

      if (!validateCpf(value) && !validateCnpj(value)) {
        setInvalidDocuments([...invalidDocuments, unformattedValue]);
        return null;
      }

      return {
        value,
        label: formatCpfCnpj(value)
      };
    },
    [invalidDocuments, setInvalidDocuments]
  );

  const downloadKml = () => {
    trackEvent("download Kml");
    const url = "https://buscar.terras.app/app/files/template_area.kml";
    window.open(url, "_blank");
  };

  const SubmitButton = ({ messageId, isLoading, className }) => {
    const isDisabled = !(
      !!(
        // !!aoiInfo &&
        // documents.length &&
        (contract.length && bacenReference.length)
      ) || !!isLoading
    );

    return (
      <button
        className={`button ${className} full-width${
          isLoading === LOADING_SAVE ? " is-loading" : ""
        }`}
        disabled={isDisabled}
        onClick={() => handleExecute(messageId === "Salvar")}
      >
        {formatMessage({ id: messageId })}
      </button>
    );
  };

  return (
    <>
      <h1>{formatMessage({ id: "nova-operacao" })}</h1>

      <div className="content-box new-operation">
        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <p>
              <>{formatMessage({ id: "mensagem-input-operacao" })}</>
            </p>
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <label className="label">
                {formatMessage({ id: "nome-operacao" })}
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  id="report-title"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-nome-operacao" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <label className="label">
                {formatMessage({ id: "referencia-bacen" })}
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  id="report-title"
                  value={bacenReference}
                  onChange={e => {
                    if (isNaN(e.target.value)) {
                      e.target.value = bacenReference;
                      return;
                    }

                    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                    setBacenReference(onlyNums);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-referencia-bacen" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <label className="label">
                {formatMessage({ id: "contrato" })}
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  id="report-title"
                  value={contract}
                  onChange={e => {
                    if (isNaN(e.target.value)) {
                      e.target.value = contract;
                      return;
                    }

                    const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                    setContract(onlyNums);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-contrato" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <label className="label">
                {formatMessage({ id: "area-nao-cultivada" })} (ha)
              </label>
              <div className="control">
                <input
                  className="input"
                  type="text"
                  id="report-title"
                  value={nonCultivatedArea}
                  onChange={e => {
                    if (!e.target.value.match(/^\d{1,}(,\d{0,4})?$/)) {
                      e.target.value = nonCultivatedArea;
                      return;
                    }

                    setNonCultivatedArea(e.target.value);
                  }}
                />
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-area-nao-cultivada" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className={`field${documents.length ? " filled" : ""}`}>
              <label className="label">
                {formatMessage({ id: "Documento-Proponentes-Participantes" })}
              </label>
              <div className="control">
                <MultiInput
                  id="report-document"
                  optionFormatter={formatDocument}
                  value={documents}
                  onChange={setDocuments}
                />
              </div>
              {invalidDocuments.length > 0 && (
                <InputErrorMessage
                  message={`${formatMessage(
                    { id: "erro-documento-invalido" },
                    { num: invalidDocuments.length }
                  )}: ${invalidDocuments.map(doc => `"${doc}"`).join(", ")}`}
                  onClear={() => setInvalidDocuments([])}
                />
              )}
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-analise-documento" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className={`field${specificUses.length ? " filled" : ""}`}>
              <label className="label">
                {formatMessage({ id: "usos-especificos" })}
              </label>
              <div className="control">
                <MultiInput
                  id="report-specific-uses"
                  value={specificUses}
                  onChange={setSpecificUses}
                />
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-analise-usos-especificos" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <div className="field">
              <div className="control">
                <input
                  type="checkbox"
                  id="pronaf-type"
                  name="pronaf-type"
                  value={isPronaf}
                  onChange={e => setIsPronaf(e.target.checked)}
                />{" "}
                {formatMessage({ id: "pronaf" })}
              </div>
            </div>
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {formatMessage({ id: "ajuda-pronaf" })}
          </div>
        </div>

        <div className="tile is-ancestor">
          <div className="tile is-6 is-vertical">
            <label className="label">{formatMessage({ id: "glebas" })}</label>
            <AoiUpload aoiInfo={aoiInfo} setAoiInfo={setAoiInfo} />
          </div>
          <div className="tile is-6 is-vertical input-info input-has-label">
            {aoiInfo ? (
              <>
                {/* {aoiInfo.Municipio && (
                  <p>
                    <span className="bold">
                      {formatMessage({ id: "municipio" })}:
                    </span>{" "}
                    {formatCityName(aoiInfo.Municipio)}
                  </p>
                )} */}
                {/* {aoiInfo.B3Area && aoiInfo.B3Area.area_ha && (
                  <p>
                    <span className="bold">
                      {formatMessage({ id: "area" })}:
                    </span>
                    {` ${formatNumber(aoiInfo.B3Area.area_ha)} ha`}
                  </p>
                )} */}
                <p>
                  <button
                    className="button secondary is-small"
                    onClick={() => setAoiInfo(null)}
                  >
                    {formatMessage({ id: "remover-arquivo" })}
                  </button>
                </p>
              </>
            ) : (
              <>
                <p>
                  {formatMessage({
                    id: "aceitamos-formatos"
                  })}
                </p>
                <li>
                  {formatMessage(
                    {
                      id: "formato-kml"
                    },
                    {
                      link: (
                        <button className="link-button" onClick={downloadKml}>
                          {formatMessage({ id: "baixar-template" })}
                        </button>
                      )
                    }
                  )}
                </li>
                <br />
                <p>
                  {formatMessage({ id: "multiplos-arquivos-por-consulta" })}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="tile is-ancestor analysis-action-buttons">
          <div className="tile is-6 is-vertical">
            <SubmitButton
              className="primary"
              isLoading={loadingStep}
              messageId="Executar"
            />
          </div>

          <div className="tile is-6 is-vertical input-info">
            <p>
              {formatMessage({ id: "ajuda-executar-operacao-1" })} &nbsp;
              <span className="bold">{formatMessage({ id: "Executar" })}</span>.
            </p>
          </div>
        </div>
      </div>

      {openModal === CONFIRMATION_MODAL && (
        <ConfirmationModal
          onClose={() => setOpenModal(null)}
          onConfirmation={handleExecute}
        />
      )}
    </>
  );
};

export default NewOperation;
